import { useState, useEffect, useRef } from "react";
import styles from "./PricingExamples.module.css";
import PricingExamplesCard from "./PricingExamplesCard";
const DEFAULT_SLIDE_DURATION = 5000;
const AVAEXPRESS_SLIDE_DURATION = 8000;

const getSlideDuration = menu =>
  menu?.providerText?.includes("آوااکسپرس")
    ? AVAEXPRESS_SLIDE_DURATION
    : DEFAULT_SLIDE_DURATION;

function PricingExamples({ title, menus }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const sliderRef = useRef(null);
  const menuRef = useRef(null);

  const totalSlides = menus.length;
  const slideDuration = getSlideDuration(menus[currentPage]);

  useEffect(() => {
    const menu = menuRef.current;
    const activeItem = menu?.children[currentPage];
    if (!menu || !activeItem || menu.scrollWidth <= menu.clientWidth) return;

    const menuRect = menu.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    const offset =
      itemRect.left + itemRect.width / 2 - (menuRect.left + menuRect.width / 2);

    menu.scrollBy({ left: offset, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "200px",
      }
    );

    observer.observe(sliderRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || totalSlides < 2) return;

    const timeoutId = setTimeout(() => {
      setCurrentPage(prev => (prev + 1) % totalSlides);
    }, slideDuration);

    return () => clearTimeout(timeoutId);
  }, [isVisible, currentPage, cycle, totalSlides, slideDuration]);

  const handleTabClick = i => {
    setCurrentPage(i);
    setCycle(prev => prev + 1);
  };

  return (
    <section
      className={`${styles.pricing} ${isVisible ? styles.pricingVisible : ""}`}
      ref={sliderRef}
    >
      <div className={styles.back} aria-hidden="true">
        <span className={`${styles.blob} ${styles.blobOne}`}></span>
        <span className={`${styles.blob} ${styles.blobTwo}`}></span>
        <span className={`${styles.blob} ${styles.blobThree}`}></span>
        <span className={`${styles.blob} ${styles.blobFour}`}></span>
        <span className={styles.ring}></span>
        <span className={styles.grid}></span>
        <span className={styles.beam}></span>
      </div>

      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
        </div>

        <div className={styles.menu} role="tablist" ref={menuRef}>
          {menus.map((menu, i) => (
            <button
              type="button"
              role="tab"
              aria-selected={currentPage === i}
              key={`prices-menu-${menu.id}`}
              className={`${styles.menuItem} ${currentPage === i ? styles.menuItemActive : ""}`}
              onClick={() => handleTabClick(i)}
            >
              <span className={styles.menuLogo}>
                <img src={menu.providerImg} alt={menu.providerText} />
              </span>

              <span className={styles.menuName}>{menu.providerText}</span>

              <span className={styles.menuProgress}>
                {currentPage === i && isVisible && (
                  <span
                    key={`${i}-${cycle}`}
                    className={styles.menuProgressBar}
                    style={{ animationDuration: `${slideDuration}ms` }}
                  ></span>
                )}
              </span>
            </button>
          ))}
        </div>

        <div className={styles.pages}>
          {menus.map((courier, idx) => (
            <div
              className={`${styles.page} ${currentPage === idx ? styles.pageActive : ""}`}
              aria-hidden={currentPage !== idx}
              key={idx}
            >
              {courier.items.map((item, index) => (
                <PricingExamplesCard
                  key={index}
                  data={item}
                  index={index}
                  providerImg={courier.providerImg}
                  providerText={courier.providerText}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default PricingExamples;
